/**
 * UserAnalytics - Privacy-focused educational analytics and behavior tracking
 */

export class UserAnalytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getOrCreateUserId();
    this.sessionData = {
      start_time: Date.now(),
      page_views: [],
      interactions: [],
      learning_progress: {},
      biome_exploration: {},
      quiz_attempts: [],
      errors: []
    };
    const storedConsent = this.checkTrackingConsent();
    this.isTrackingEnabled = storedConsent === true;
    this.hasConsentPreference = storedConsent !== null;
    this.batchSize = 20;
    this.flushInterval = 30000; // 30 seconds
    this.offlineQueue = [];
    this.initializeAnalytics();
  }

  /**
   * Initialize analytics system
   */
  initializeAnalytics() {
    // Set up periodic data flushing
    this.setupPeriodicFlush();

    // Set up session tracking
    this.trackSessionStart();

    // Set up beforeunload to save session data
    window.addEventListener('beforeunload', () => {
      this.trackSessionEnd();
      this.flushPendingData(true); // Synchronous flush on page unload
    });

    // Set up visibility change tracking
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden', { timestamp: Date.now() });
      } else {
        this.trackEvent('page_visible', { timestamp: Date.now() });
      }
    });

    console.log('📊 User Analytics initialized (Privacy-focused)');
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${  Date.now()  }_${  Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get or create anonymous user ID (stored locally)
   */
  getOrCreateUserId() {
    const storageKey = 'landgrid_user_id';
    let userId = localStorage.getItem(storageKey);

    if (!userId) {
      userId = `user_${  Date.now()  }_${  Math.random().toString(36).substr(2, 9)}`;
      try {
        localStorage.setItem(storageKey, userId);
      } catch (error) {
        // If localStorage is not available, use session-based ID
        userId = `session_user_${  this.sessionId}`;
      }
    }

    return userId;
  }

  /**
   * Check if user has consented to analytics tracking
   */
  checkTrackingConsent() {
    try {
      const consent = localStorage.getItem('landgrid_analytics_consent');
      if (consent === null) {
        return null;
      }
      return consent === 'true';
    } catch (error) {
      // If localStorage is not available, default to privacy-friendly mode
      return false;
    }
  }

  /**
   * Set user consent for analytics
   */
  setTrackingConsent(consent) {
    this.isTrackingEnabled = consent;
    this.hasConsentPreference = true;
    try {
      localStorage.setItem('landgrid_analytics_consent', consent.toString());
    } catch (error) {
      console.warn('Could not save analytics consent preference');
    }

    if (consent) {
      this.trackEvent('analytics_consent_granted');
    } else {
      this.trackEvent('analytics_consent_revoked');
      this.clearStoredData();
    }
  }

  /**
   * Track session start
   */
  trackSessionStart() {
    const sessionInfo = {
      session_id: this.sessionId,
      user_id: this.isTrackingEnabled ? this.userId : 'anonymous',
      start_time: Date.now(),
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      referrer: document.referrer || 'direct'
    };

    this.sessionData.session_info = sessionInfo;
    this.trackEvent('session_start', sessionInfo);
  }

  /**
   * Track session end
   */
  trackSessionEnd() {
    const sessionDuration = Date.now() - this.sessionData.start_time;
    const endInfo = {
      session_id: this.sessionId,
      end_time: Date.now(),
      duration_ms: sessionDuration,
      total_interactions: this.sessionData.interactions.length,
      biomes_explored: Object.keys(this.sessionData.biome_exploration).length,
      quizzes_completed: this.sessionData.quiz_attempts.filter(q => q.completed).length
    };

    this.sessionData.session_end = endInfo;
    this.trackEvent('session_end', endInfo);
  }

  /**
   * Track page view
   */
  trackPageView(page, additionalData = {}) {
    if (!this.isTrackingEnabled) return;

    const pageView = {
      page,
      timestamp: Date.now(),
      url: window.location.href,
      ...additionalData
    };

    this.sessionData.page_views.push(pageView);
    this.trackEvent('page_view', pageView);
  }

  /**
   * Track user interaction
   */
  trackInteraction(action, target, additionalData = {}) {
    if (!this.isTrackingEnabled) return;

    const interaction = {
      action,
      target,
      timestamp: Date.now(),
      session_id: this.sessionId,
      ...additionalData
    };

    this.sessionData.interactions.push(interaction);
    this.trackEvent('interaction', interaction);
  }

  /**
   * Track biome exploration
   */
  trackBiomeExploration(biome, coordinates, method = 'click') {
    if (!this.isTrackingEnabled) return;

    const exploration = {
      biome,
      coordinates: {
        lat: Math.round(coordinates.lat * 100) / 100, // Round for privacy
        lon: Math.round(coordinates.lon * 100) / 100
      },
      method, // click, search, navigation
      timestamp: Date.now(),
      session_id: this.sessionId
    };

    // Update session biome exploration data
    if (!this.sessionData.biome_exploration[biome]) {
      this.sessionData.biome_exploration[biome] = {
        first_visit: Date.now(),
        visit_count: 0,
        total_time: 0,
        last_visit: Date.now()
      };
    }

    this.sessionData.biome_exploration[biome].visit_count++;
    this.sessionData.biome_exploration[biome].last_visit = Date.now();

    this.trackEvent('biome_exploration', exploration);
  }

  /**
   * Track quiz attempt
   */
  trackQuizAttempt(biome, questionId, userAnswer, correctAnswer, isCorrect, timeSpent) {
    if (!this.isTrackingEnabled) return;

    const attempt = {
      biome,
      question_id: questionId,
      is_correct: isCorrect,
      time_spent_ms: timeSpent,
      timestamp: Date.now(),
      session_id: this.sessionId,
      difficulty: 'unknown' // Will be filled by education engine
    };

    this.sessionData.quiz_attempts.push(attempt);
    this.trackEvent('quiz_attempt', attempt);

    // Update learning progress
    if (!this.sessionData.learning_progress[biome]) {
      this.sessionData.learning_progress[biome] = {
        questions_attempted: 0,
        questions_correct: 0,
        total_time_spent: 0,
        average_time_per_question: 0
      };
    }

    const progress = this.sessionData.learning_progress[biome];
    progress.questions_attempted++;
    progress.total_time_spent += timeSpent;
    progress.average_time_per_question = progress.total_time_spent / progress.questions_attempted;

    if (isCorrect) {
      progress.questions_correct++;
    }
  }

  /**
   * Track quiz completion
   */
  trackQuizCompletion(biome, score, totalQuestions, timeSpent, difficulty) {
    if (!this.isTrackingEnabled) return;

    const completion = {
      biome,
      score,
      total_questions: totalQuestions,
      percentage: Math.round((score / totalQuestions) * 100),
      time_spent_ms: timeSpent,
      difficulty,
      timestamp: Date.now(),
      session_id: this.sessionId
    };

    // Update the last quiz attempt as completed
    const lastQuiz = this.sessionData.quiz_attempts[this.sessionData.quiz_attempts.length - 1];
    if (lastQuiz) {
      lastQuiz.completed = true;
      lastQuiz.final_score = score;
      lastQuiz.final_percentage = completion.percentage;
    }

    this.trackEvent('quiz_completion', completion);
  }

  /**
   * Track learning achievement
   */
  trackAchievement(type, achievement, biome = null) {
    if (!this.isTrackingEnabled) return;

    const achievementData = {
      type, // 'quiz_mastery', 'biome_explorer', 'time_spent', etc.
      achievement,
      biome,
      timestamp: Date.now(),
      session_id: this.sessionId
    };

    this.trackEvent('achievement', achievementData);
  }

  /**
   * Track error or problem
   */
  trackError(error, context = '', severity = 'low') {
    const errorData = {
      error: error.message || error.toString(),
      context,
      severity,
      stack: error.stack ? error.stack.substring(0, 500) : null, // Limit stack trace size
      timestamp: Date.now(),
      session_id: this.sessionId,
      user_agent: navigator.userAgent
    };

    this.sessionData.errors.push(errorData);
    this.trackEvent('error', errorData);
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric, value, context = '') {
    if (!this.isTrackingEnabled) return;

    const performanceData = {
      metric, // 'load_time', 'interaction_delay', 'memory_usage', etc.
      value,
      context,
      timestamp: Date.now(),
      session_id: this.sessionId
    };

    this.trackEvent('performance', performanceData);
  }

  /**
   * Track offline content usage
   */
  trackOfflineContentUsage(contentPack, action, additionalData = {}) {
    if (!this.isTrackingEnabled) return;

    const usage = {
      content_pack: contentPack,
      action, // 'download', 'access', 'complete'
      timestamp: Date.now(),
      session_id: this.sessionId,
      ...additionalData
    };

    this.trackEvent('offline_content', usage);
  }

  /**
   * Core event tracking method
   */
  trackEvent(eventType, eventData) {
    const event = {
      event_type: eventType,
      data: eventData,
      timestamp: Date.now(),
      session_id: this.sessionId
    };

    // Add to offline queue for batch processing
    this.offlineQueue.push(event);

    // Flush if batch size reached
    if (this.offlineQueue.length >= this.batchSize) {
      this.flushPendingData();
    }
  }

  /**
   * Setup periodic data flushing
   */
  setupPeriodicFlush() {
    setInterval(() => {
      if (this.offlineQueue.length > 0) {
        this.flushPendingData();
      }
    }, this.flushInterval);
  }

  /**
   * Flush pending analytics data
   */
  async flushPendingData(synchronous = false) {
    if (this.offlineQueue.length === 0) return;

    const dataToFlush = [...this.offlineQueue];
    this.offlineQueue = [];

    try {
      // In a real application, this would send to an analytics endpoint
      // For now, we'll store locally and log for debugging
      await this.storeAnalyticsData(dataToFlush, synchronous);

      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Analytics data flushed:', dataToFlush.length, 'events');
      }
    } catch (error) {
      // If flush fails, put data back in queue
      this.offlineQueue.unshift(...dataToFlush);
      console.warn('Failed to flush analytics data:', error);
    }
  }

  /**
   * Store analytics data locally (for offline capability)
   */
  async storeAnalyticsData(data, synchronous = false) {
    const storageKey = `landgrid_analytics_${Date.now()}`;
    const analyticsData = {
      session_id: this.sessionId,
      user_id: this.isTrackingEnabled ? this.userId : 'anonymous',
      events: data,
      stored_at: Date.now()
    };

    try {
      if (synchronous) {
        // Use synchronous storage for page unload
        localStorage.setItem(storageKey, JSON.stringify(analyticsData));
      } else {
        // Use IndexedDB for better performance
        await this.storeInIndexedDB(storageKey, analyticsData);
      }
    } catch (error) {
      // Fallback to localStorage if IndexedDB fails
      try {
        localStorage.setItem(storageKey, JSON.stringify(analyticsData));
      } catch (localStorageError) {
        console.warn('Could not store analytics data:', error, localStorageError);
      }
    }
  }

  /**
   * Store data in IndexedDB
   */
  async storeInIndexedDB(key, data) {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('LandGridAnalytics', 1);

      request.onerror = () => reject(request.error);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('analytics')) {
          db.createObjectStore('analytics', { keyPath: 'id' });
        }
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['analytics'], 'readwrite');
        const store = transaction.objectStore('analytics');

        const putRequest = store.put({ id: key, ...data });
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
    });
  }

  /**
   * Get learning analytics summary
   */
  getLearningAnalytics() {
    const summary = {
      session_duration: Date.now() - this.sessionData.start_time,
      biomes_explored: Object.keys(this.sessionData.biome_exploration).length,
      total_interactions: this.sessionData.interactions.length,
      quiz_attempts: this.sessionData.quiz_attempts.length,
      quiz_completions: this.sessionData.quiz_attempts.filter(q => q.completed).length,
      learning_progress: { ...this.sessionData.learning_progress },
      biome_exploration: { ...this.sessionData.biome_exploration },
      most_explored_biome: this.getMostExploredBiome(),
      average_quiz_score: this.getAverageQuizScore(),
      strengths: this.identifyLearningStrengths(),
      areas_for_improvement: this.identifyImprovementAreas()
    };

    return summary;
  }

  /**
   * Get most explored biome
   */
  getMostExploredBiome() {
    const exploration = this.sessionData.biome_exploration;
    let mostExplored = null;
    let maxVisits = 0;

    for (const [biome, data] of Object.entries(exploration)) {
      if (data.visit_count > maxVisits) {
        maxVisits = data.visit_count;
        mostExplored = biome;
      }
    }

    return mostExplored;
  }

  /**
   * Get average quiz score
   */
  getAverageQuizScore() {
    const completedQuizzes = this.sessionData.quiz_attempts.filter(q => q.completed);
    if (completedQuizzes.length === 0) return 0;

    const totalScore = completedQuizzes.reduce((sum, quiz) => sum + (quiz.final_percentage || 0), 0);
    return Math.round(totalScore / completedQuizzes.length);
  }

  /**
   * Identify learning strengths
   */
  identifyLearningStrengths() {
    const strengths = [];
    const progress = this.sessionData.learning_progress;

    for (const [biome, data] of Object.entries(progress)) {
      const accuracy = data.questions_correct / data.questions_attempted;
      if (accuracy >= 0.8 && data.questions_attempted >= 3) {
        strengths.push(`Strong performance in ${biome} (${Math.round(accuracy * 100)}% accuracy)`);
      }
    }

    return strengths;
  }

  /**
   * Identify areas for improvement
   */
  identifyImprovementAreas() {
    const improvements = [];
    const progress = this.sessionData.learning_progress;

    for (const [biome, data] of Object.entries(progress)) {
      const accuracy = data.questions_correct / data.questions_attempted;
      if (accuracy < 0.6 && data.questions_attempted >= 3) {
        improvements.push(`Consider reviewing ${biome} concepts (${Math.round(accuracy * 100)}% accuracy)`);
      }
    }

    return improvements;
  }

  /**
   * Clear all stored analytics data
   */
  clearStoredData() {
    try {
      // Clear localStorage analytics data
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('landgrid_analytics_') && key !== 'landgrid_analytics_consent') {
          localStorage.removeItem(key);
        }
      });

      // Clear IndexedDB analytics data
      const request = indexedDB.open('LandGridAnalytics', 1);
      request.onsuccess = (event) => {
        const db = event.target.result;
        const transaction = db.transaction(['analytics'], 'readwrite');
        const store = transaction.objectStore('analytics');
        store.clear();
      };
    } catch (error) {
      console.warn('Could not clear analytics data:', error);
    }
  }

  /**
   * Generate privacy report
   */
  generatePrivacyReport() {
    return {
      tracking_enabled: this.isTrackingEnabled,
      user_id: this.isTrackingEnabled ? this.userId : 'anonymous',
      session_id: this.sessionId,
      data_collection: {
        interactions: this.sessionData.interactions.length,
        biome_explorations: Object.keys(this.sessionData.biome_exploration).length,
        quiz_attempts: this.sessionData.quiz_attempts.length,
        errors_logged: this.sessionData.errors.length
      },
      privacy_features: [
        'Anonymous user IDs',
        'Local data storage',
        'Consent-based tracking',
        'Coordinate rounding for privacy',
        'No personal information collected',
        'User-controlled data deletion'
      ],
      data_retention: 'Data stored locally only, cleared when consent is revoked'
    };
  }

  /**
   * Initialize analytics system
   */
  async initialize() {
    // Check if user needs to provide consent
    if (this.checkTrackingConsent() === null) {
      // Show consent dialog (this would be handled by UI controller)
      console.log('📊 Analytics consent required - privacy-first approach');
    }

    console.log('📊 User Analytics initialized');
    console.log('🔒 Privacy-focused tracking:', this.isTrackingEnabled ? 'enabled' : 'disabled');
  }
}
